import { useState, useEffect, useCallback } from 'react';
import { 
  Search, Filter, Users, Building2, MapPin, Shield,
  CheckCircle, Clock, AlertTriangle, XCircle, Eye, EyeOff,
  FileText, MessageSquare, ChevronRight, RefreshCw,
  DollarSign, Phone, Mail, Globe, Star, AlertCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Separator } from '../ui/separator';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status configuration
const ADMIN_STATUS_CONFIG = {
  registered: { label: 'Registrato', color: 'bg-gray-100 text-gray-700', icon: Clock },
  docs_pending: { label: 'Documenti Mancanti', color: 'bg-amber-100 text-amber-700', icon: FileText },
  under_review: { label: 'In Revisione', color: 'bg-blue-100 text-blue-700', icon: Eye },
  contact_pending: { label: 'Da Contattare', color: 'bg-purple-100 text-purple-700', icon: Phone },
  pre_verified: { label: 'Pre-Verificato', color: 'bg-cyan-100 text-cyan-700', icon: CheckCircle },
  approved: { label: 'Approvato', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  rejected: { label: 'Rifiutato', color: 'bg-red-100 text-red-700', icon: XCircle },
  suspended: { label: 'Sospeso', color: 'bg-orange-100 text-orange-700', icon: AlertTriangle }
};

const RISK_LEVEL_CONFIG = {
  low: { label: 'Basso', color: 'bg-green-50 text-green-700', icon: Shield },
  medium: { label: 'Medio', color: 'bg-amber-50 text-amber-700', icon: Shield },
  high: { label: 'Alto', color: 'bg-red-50 text-red-700', icon: AlertTriangle }
};

const SOURCE_CONFIG = {
  provider_portal: { label: 'Portale', color: 'bg-blue-50 text-blue-700' },
  admin_manual: { label: 'Inserito Admin', color: 'bg-purple-50 text-purple-700' },
  import_curated: { label: 'Import Curato', color: 'bg-green-50 text-green-700' },
  google_places: { label: 'Google Places', color: 'bg-amber-50 text-amber-700' }
};

export function ProviderAdminPanel() {
  // State
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [policies, setPolicies] = useState({});
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // Search filters
  const [searchText, setSearchText] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterRisk, setFilterRisk] = useState('');
  const [filterMacroArea, setFilterMacroArea] = useState('');
  const [showChiaraOnly, setShowChiaraOnly] = useState(false);
  
  // Note input
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState('general');

  // Load data
  const loadDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/providers/dashboard`);
      const data = await res.json();
      setDashboard(data);
      setPolicies(data.policies || {});
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    }
  }, []);

  const searchProviders = useCallback(async () => {
    setLoading(true);
    try {
      const body = {
        search_text: searchText || null,
        type: filterType || null,
        admin_status: filterStatus ? [filterStatus] : null,
        risk_level: filterRisk || null,
        macro_area: filterMacroArea || null,
        visible_to_chiara: showChiaraOnly ? true : null,
        limit: 100
      };
      
      const res = await fetch(`${API}/admin/providers/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      setProviders(data.providers || []);
    } catch (err) {
      console.error('Search failed:', err);
      toast.error('Errore nella ricerca');
    } finally {
      setLoading(false);
    }
  }, [searchText, filterType, filterStatus, filterRisk, filterMacroArea, showChiaraOnly]);

  const loadProviderDetail = async (profileId) => {
    setDetailLoading(true);
    try {
      const res = await fetch(`${API}/admin/providers/${profileId}/detail`);
      const data = await res.json();
      setSelectedProvider(data);
    } catch (err) {
      console.error('Failed to load detail:', err);
      toast.error('Errore caricamento dettagli');
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
    searchProviders();
  }, [loadDashboard, searchProviders]);

  // Actions
  const changeStatus = async (profileId, newStatus, notes = '') => {
    try {
      const res = await fetch(`${API}/admin/providers/${profileId}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_status: newStatus, notes, changed_by: 'admin' })
      });
      
      if (!res.ok) throw new Error('Status change failed');
      
      toast.success(`Stato aggiornato: ${ADMIN_STATUS_CONFIG[newStatus]?.label}`);
      await loadProviderDetail(profileId);
      searchProviders();
    } catch (err) {
      toast.error('Errore cambio stato');
    }
  };

  const toggleVisibility = async (profileId, visible) => {
    try {
      const res = await fetch(`${API}/admin/providers/${profileId}/visibility`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visible_to_chiara: visible, changed_by: 'admin' })
      });
      
      if (!res.ok) throw new Error('Visibility change failed');
      
      toast.success(visible ? 'Ora visibile a Chiara' : 'Nascosto da Chiara');
      await loadProviderDetail(profileId);
      searchProviders();
    } catch (err) {
      toast.error('Errore cambio visibilità');
    }
  };

  const addNote = async (profileId) => {
    if (!newNote.trim()) return;
    
    try {
      const res = await fetch(`${API}/admin/providers/${profileId}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: newNote, note_type: noteType, author: 'admin' })
      });
      
      if (!res.ok) throw new Error('Add note failed');
      
      toast.success('Nota aggiunta');
      setNewNote('');
      await loadProviderDetail(profileId);
    } catch (err) {
      toast.error('Errore aggiunta nota');
    }
  };

  // Dashboard stats
  const renderDashboard = () => (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      <Card className="bg-white">
        <CardContent className="pt-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold">{dashboard?.total_providers || 0}</p>
              <p className="text-xs text-gray-500">Totali</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card className="bg-white">
        <CardContent className="pt-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Clock className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold">{dashboard?.pending_review || 0}</p>
              <p className="text-xs text-gray-500">Da Revisione</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card className="bg-white">
        <CardContent className="pt-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Eye className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold">{dashboard?.visible_to_chiara || 0}</p>
              <p className="text-xs text-gray-500">Visibili a Chiara</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card className="bg-white">
        <CardContent className="pt-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold">{dashboard?.risk_counts?.high || 0}</p>
              <p className="text-xs text-gray-500">Alto Rischio</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Search filters
  const renderFilters = () => (
    <Card className="mb-6">
      <CardContent className="pt-4">
        <div className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-[200px]">
            <Input
              placeholder="Cerca per nome, servizi..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="w-full"
              data-testid="admin-search-input"
            />
          </div>
          
          <Select value={filterType || "_all"} onValueChange={(v) => setFilterType(v === "_all" ? "" : v)}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Categoria" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Tutte</SelectItem>
              <SelectItem value="accommodation">Alloggi</SelectItem>
              <SelectItem value="restaurant">Ristoranti</SelectItem>
              <SelectItem value="guide">Guide</SelectItem>
              <SelectItem value="transport">Trasporti</SelectItem>
              <SelectItem value="babysitting">Babysitter</SelectItem>
              <SelectItem value="wellness">Wellness</SelectItem>
              <SelectItem value="experience">Esperienze</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={filterStatus || "_all"} onValueChange={(v) => setFilterStatus(v === "_all" ? "" : v)}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Stato Admin" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Tutti</SelectItem>
              <SelectItem value="registered">Registrato</SelectItem>
              <SelectItem value="docs_pending">Docs Mancanti</SelectItem>
              <SelectItem value="under_review">In Revisione</SelectItem>
              <SelectItem value="contact_pending">Da Contattare</SelectItem>
              <SelectItem value="approved">Approvato</SelectItem>
              <SelectItem value="rejected">Rifiutato</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={filterRisk || "_all"} onValueChange={(v) => setFilterRisk(v === "_all" ? "" : v)}>
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="Rischio" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Tutti</SelectItem>
              <SelectItem value="low">Basso</SelectItem>
              <SelectItem value="medium">Medio</SelectItem>
              <SelectItem value="high">Alto</SelectItem>
            </SelectContent>
          </Select>
          
          <Button 
            variant={showChiaraOnly ? "default" : "outline"}
            onClick={() => setShowChiaraOnly(!showChiaraOnly)}
            className="gap-2"
          >
            <Eye className="h-4 w-4" />
            Solo Chiara
          </Button>
          
          <Button onClick={searchProviders} disabled={loading}>
            <Search className="h-4 w-4 mr-2" />
            Cerca
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  // Provider list
  const renderProviderList = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {providers.map((provider) => {
        const statusConfig = ADMIN_STATUS_CONFIG[provider.admin_status] || ADMIN_STATUS_CONFIG.registered;
        const riskConfig = RISK_LEVEL_CONFIG[provider.risk_level] || RISK_LEVEL_CONFIG.medium;
        const sourceConfig = SOURCE_CONFIG[provider.source] || SOURCE_CONFIG.provider_portal;
        const StatusIcon = statusConfig.icon;
        
        return (
          <Card 
            key={provider.id}
            className={`cursor-pointer hover:shadow-md transition-shadow ${
              provider.visible_to_chiara ? 'border-l-4 border-l-green-500' : ''
            }`}
            onClick={() => loadProviderDetail(provider.id)}
            data-testid={`provider-card-${provider.id}`}
          >
            <CardContent className="pt-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-medium text-umbria-charcoal truncate">
                      {provider.business_name}
                    </h3>
                    {provider.visible_to_chiara && (
                      <Eye className="h-4 w-4 text-green-500 flex-shrink-0" />
                    )}
                  </div>
                  
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    <Badge className={statusConfig.color} variant="secondary">
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {statusConfig.label}
                    </Badge>
                    <Badge className={riskConfig.color} variant="secondary">
                      {riskConfig.label}
                    </Badge>
                    <Badge className={sourceConfig.color} variant="secondary">
                      {sourceConfig.label}
                    </Badge>
                  </div>
                  
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Building2 className="h-3 w-3" />
                      {provider.type}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {provider.comune || provider.macro_area}
                    </span>
                  </div>
                </div>
                
                <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
              </div>
            </CardContent>
          </Card>
        );
      })}
      
      {providers.length === 0 && !loading && (
        <div className="col-span-2 text-center py-12 text-gray-500">
          Nessun provider trovato con i filtri selezionati
        </div>
      )}
    </div>
  );

  // Provider detail modal
  const renderProviderDetail = () => {
    if (!selectedProvider) return null;
    
    const profile = selectedProvider.profile;
    const policy = selectedProvider.policy;
    const notes = selectedProvider.notes || [];
    const statusConfig = ADMIN_STATUS_CONFIG[profile.admin_status] || ADMIN_STATUS_CONFIG.registered;
    const riskConfig = RISK_LEVEL_CONFIG[profile.risk_level] || RISK_LEVEL_CONFIG.medium;
    
    return (
      <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto">
        <div 
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={() => setSelectedProvider(null)}
        />
        
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl my-8 max-h-[90vh] overflow-y-auto">
          {detailLoading ? (
            <div className="p-12 text-center">
              <RefreshCw className="h-8 w-8 animate-spin text-umbria-olive mx-auto" />
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="sticky top-0 bg-white border-b p-4 z-10">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold">{profile.business_name}</h2>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <Badge className={statusConfig.color}>
                        {statusConfig.label}
                      </Badge>
                      <Badge className={riskConfig.color}>
                        Rischio: {riskConfig.label}
                      </Badge>
                      {/* Email Verification Status */}
                      {selectedProvider.user?.is_verified ? (
                        <Badge className="bg-green-100 text-green-700">
                          <Mail className="h-3 w-3 mr-1" />
                          Email Verificata
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-700">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          Email Non Verificata
                        </Badge>
                      )}
                      {profile.visible_to_chiara ? (
                        <Badge className="bg-green-100 text-green-700">
                          <Eye className="h-3 w-3 mr-1" />
                          Visibile a Chiara
                        </Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-700">
                          <EyeOff className="h-3 w-3 mr-1" />
                          Non visibile
                        </Badge>
                      )}
                    </div>
                  </div>
                  <Button variant="ghost" onClick={() => setSelectedProvider(null)}>
                    Chiudi
                  </Button>
                </div>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Policy Alert for High Risk */}
                {profile.risk_level === 'high' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-red-800">Categoria Sensibile</h4>
                        <p className="text-sm text-red-600 mt-1 whitespace-pre-line">
                          {policy?.admin_notes || 'Richiede verifica manuale approfondita.'}
                        </p>
                        
                        {/* Sensitive flags */}
                        <div className="flex flex-wrap gap-2 mt-3">
                          {profile.manual_vetting_required && (
                            <Badge variant="outline" className="border-red-300 text-red-700">
                              Vetting Manuale Obbligatorio
                            </Badge>
                          )}
                          {profile.background_check_off_platform && profile.background_check_off_platform !== 'not_applicable' && (
                            <Badge 
                              variant="outline" 
                              className={profile.background_check_off_platform === 'verified' 
                                ? 'border-green-300 text-green-700' 
                                : 'border-amber-300 text-amber-700'
                              }
                            >
                              Background Check: {profile.background_check_off_platform === 'verified' ? 'Verificato' : 'Pendente (off-platform)'}
                            </Badge>
                          )}
                        </div>
                        
                        {policy?.required_docs?.length > 0 && (
                          <div className="mt-3">
                            <span className="text-xs font-medium text-red-700">Documenti richiesti:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {policy.required_docs.map(doc => (
                                <Badge 
                                  key={doc} 
                                  variant="outline"
                                  className={selectedProvider.docs_status?.[doc] 
                                    ? 'border-green-300 text-green-700' 
                                    : 'border-red-300 text-red-700'
                                  }
                                >
                                  {doc.replace(/_/g, ' ')}
                                  {selectedProvider.docs_status?.[doc] ? ' ✓' : ' ✗'}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-500">Tipo</label>
                    <p className="text-sm">{profile.type} / {profile.subtype}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Location</label>
                    <p className="text-sm">{profile.comune}, {profile.macro_area}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Email</label>
                    <p className="text-sm">{profile.contact_email}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Telefono</label>
                    <p className="text-sm">{profile.contact_phone}</p>
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs font-medium text-gray-500">Descrizione</label>
                    <p className="text-sm text-gray-700">{profile.short_description}</p>
                  </div>
                </div>
                
                {/* Commission */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium flex items-center gap-2 mb-2">
                    <DollarSign className="h-4 w-4" />
                    Commissione
                  </h4>
                  <p className="text-sm">
                    {profile.commission?.model === 'percentage' 
                      ? `${profile.commission?.rate || policy?.default_commission?.rate || 15}%`
                      : profile.commission?.model === 'fixed_fee'
                        ? `€${profile.commission?.rate || 5} fisso`
                        : `${profile.commission?.rate || 15}% (min €${profile.commission?.minimum_fee || 5})`
                    }
                  </p>
                </div>
                
                {/* Documents Section */}
                {selectedProvider?.documents?.length > 0 && (
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="font-medium flex items-center gap-2 mb-3">
                      <FileText className="h-4 w-4" />
                      Documenti Caricati ({selectedProvider.documents.length})
                    </h4>
                    <div className="space-y-2">
                      {selectedProvider.documents.map(doc => (
                        <div 
                          key={doc.id}
                          className={`flex items-center justify-between p-2 rounded border ${
                            doc.verified 
                              ? 'bg-green-50 border-green-200' 
                              : 'bg-white border-gray-200'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-gray-500" />
                            <div>
                              <p className="text-sm font-medium">{doc.doc_type?.replace(/_/g, ' ')}</p>
                              <p className="text-xs text-gray-500">
                                {new Date(doc.uploaded_at).toLocaleDateString('it-IT')}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <a
                              href={`${API.replace('/api', '')}${doc.url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1 hover:bg-gray-200 rounded"
                              title="Visualizza"
                            >
                              <Eye className="h-4 w-4" />
                            </a>
                            {doc.verified ? (
                              <Badge className="bg-green-100 text-green-700">Verificato</Badge>
                            ) : (
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs"
                                  onClick={async () => {
                                    try {
                                      await fetch(`${API}/admin/documents/${doc.id}/verify`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ verified: true, admin_id: 'admin' })
                                      });
                                      toast.success('Documento verificato');
                                      loadProviderDetail(profile.id);
                                    } catch (err) {
                                      toast.error('Errore verifica documento');
                                    }
                                  }}
                                >
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Verifica
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  className="h-7 text-xs"
                                  onClick={async () => {
                                    try {
                                      await fetch(`${API}/admin/documents/${doc.id}/verify`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ verified: false, admin_id: 'admin', notes: 'Documento non valido' })
                                      });
                                      toast.success('Documento rifiutato');
                                      loadProviderDetail(profile.id);
                                    } catch (err) {
                                      toast.error('Errore rifiuto documento');
                                    }
                                  }}
                                >
                                  <XCircle className="h-3 w-3" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <Separator />
                
                {/* Status Actions */}
                <div>
                  <h4 className="font-medium mb-3">Azioni</h4>
                  <div className="flex flex-wrap gap-2">
                    {profile.admin_status !== 'approved' && (
                      <Button 
                        onClick={() => changeStatus(profile.id, 'under_review')}
                        variant="outline"
                        className="gap-2"
                      >
                        <Eye className="h-4 w-4" />
                        In Revisione
                      </Button>
                    )}
                    
                    {profile.admin_status !== 'contact_pending' && (
                      <Button 
                        onClick={() => changeStatus(profile.id, 'contact_pending')}
                        variant="outline"
                        className="gap-2"
                      >
                        <Phone className="h-4 w-4" />
                        Da Contattare
                      </Button>
                    )}
                    
                    {profile.admin_status !== 'approved' && (
                      <Button 
                        onClick={() => changeStatus(profile.id, 'approved')}
                        className="gap-2 bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="h-4 w-4" />
                        Approva
                      </Button>
                    )}
                    
                    {profile.admin_status !== 'rejected' && (
                      <Button 
                        onClick={() => changeStatus(profile.id, 'rejected', 'Rifiutato da admin')}
                        variant="destructive"
                        className="gap-2"
                      >
                        <XCircle className="h-4 w-4" />
                        Rifiuta
                      </Button>
                    )}
                    
                    <Button
                      onClick={() => toggleVisibility(profile.id, !profile.visible_to_chiara)}
                      variant="outline"
                      className={`gap-2 ${profile.visible_to_chiara 
                        ? 'border-red-300 text-red-700 hover:bg-red-50' 
                        : 'border-green-300 text-green-700 hover:bg-green-50'}`}
                    >
                      {profile.visible_to_chiara ? (
                        <><EyeOff className="h-4 w-4" /> Nascondi da Chiara</>
                      ) : (
                        <><Eye className="h-4 w-4" /> Rendi Visibile a Chiara</>
                      )}
                    </Button>
                  </div>
                </div>
                
                <Separator />
                
                {/* Internal Notes */}
                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Note Interne ({notes.length})
                  </h4>
                  
                  {/* Add note */}
                  <div className="flex gap-2 mb-4">
                    <Select value={noteType} onValueChange={setNoteType}>
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">Generale</SelectItem>
                        <SelectItem value="contact">Contatto</SelectItem>
                        <SelectItem value="verification">Verifica</SelectItem>
                        <SelectItem value="warning">Avviso</SelectItem>
                        <SelectItem value="positive">Positivo</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder="Aggiungi nota interna..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      className="flex-1"
                    />
                    <Button onClick={() => addNote(profile.id)}>
                      Aggiungi
                    </Button>
                  </div>
                  
                  {/* Notes list */}
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {notes.map((note) => (
                      <div 
                        key={note.id}
                        className={`p-3 rounded-lg text-sm ${
                          note.note_type === 'warning' ? 'bg-red-50' :
                          note.note_type === 'positive' ? 'bg-green-50' :
                          note.note_type === 'contact' ? 'bg-purple-50' :
                          'bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-gray-500">
                            {note.author} • {note.note_type}
                          </span>
                          <span className="text-xs text-gray-400">
                            {new Date(note.created_at).toLocaleDateString('it-IT')}
                          </span>
                        </div>
                        <p className="text-gray-700">{note.content}</p>
                      </div>
                    ))}
                    
                    {notes.length === 0 && (
                      <p className="text-sm text-gray-400 text-center py-4">
                        Nessuna nota interna
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="provider-admin-panel">
      {renderDashboard()}
      {renderFilters()}
      
      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-umbria-olive mx-auto mb-3" />
          <p className="text-gray-500">Caricamento provider...</p>
        </div>
      ) : (
        renderProviderList()
      )}
      
      {renderProviderDetail()}
    </div>
  );
}
